import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { updateGallery, fetchGalleryInfo, getCoverPublicUrl } from '../lib/api'
import LoadingDots from './LoadingDots'

const LocationPicker = lazy(() => import('./LocationPicker'))

interface GalleryEditFormProps {
  galleryId: string
  onDone: () => void
  onClose: () => void
}

export default function GalleryEditForm({ galleryId, onDone, onClose }: GalleryEditFormProps) {
  const [name, setName] = useState('')
  const [lat, setLat] = useState<number | null>(null)
  const [lng, setLng] = useState<number | null>(null)
  const [currentCoverUrl, setCurrentCoverUrl] = useState<string | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [coverPreview, setCoverPreview] = useState<string | null>(null)
  const [converting, setConverting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const info = await fetchGalleryInfo(galleryId)
        setName(info.name)
        setLat(info.locationLat)
        setLng(info.locationLng)
        if (info.landingImageUrl) {
          setCurrentCoverUrl(getCoverPublicUrl(info.landingImageUrl))
        }
      } catch (err) {
        setError((err as Error).message)
      } finally {
        setLoading(false)
      }
    })()
  }, [galleryId])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    if (!file) return
    setConverting(true)
    try {
      const { isHeic, heicTo } = await import('heic-to')
      if (await isHeic(file)) {
        const blob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.82 })
        const name = file.name.replace(/\.[^.]+$/, '.jpg')
        setCoverFile(new File([blob], name, { type: 'image/jpeg' }))
        setCoverPreview(URL.createObjectURL(blob))
      } else {
        setCoverFile(file)
        setCoverPreview(URL.createObjectURL(file))
      }
    } catch {
      setCoverFile(file)
      setCoverPreview(URL.createObjectURL(file))
    } finally {
      setConverting(false)
    }
  }

  const handleSubmit = async () => {
    if (lat == null || lng == null) return

    setSaving(true)
    setError(null)
    try {
      await updateGallery(galleryId, {
        name: name.trim() || undefined,
        locationLat: lat,
        locationLng: lng,
        coverImageFile: coverFile ?? undefined,
      })
      onDone()
    } catch (err) {
      setError((err as Error).message)
      setSaving(false)
    }
  }

  const canSave = name.trim().length > 0 && lat != null && lng != null && !converting

  if (loading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <LoadingDots />
            Laster...
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="relative flex items-center justify-center">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 text-gray-500 hover:text-white transition-colors p-1 rounded"
          aria-label="Lukk"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h2 className="text-lg font-semibold text-white">Rediger galleri</h2>
      </div>

      {error && (
        <div className="rounded-lg bg-red-900 border border-red-700 p-3 text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Name */}
      <div className="flex flex-col gap-1">
        <label className="text-gray-400 text-sm font-medium">Navn på galleriet</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && canSave) handleSubmit() }}
          placeholder="Appelsinhaugen"
          autoFocus
          className="bg-gray-800 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
        />
      </div>

      {/* Location */}
      <div className="flex flex-col gap-1">
        <label className="text-gray-400 text-sm font-medium">Sted</label>
        <Suspense fallback={<div className="h-64 rounded-lg bg-gray-800 animate-pulse" />}>
          <LocationPicker
            lat={lat}
            lng={lng}
            onChange={(newLat, newLng) => { setLat(newLat); setLng(newLng) }}
          />
        </Suspense>
      </div>

      {/* Cover image */}
      <div className="flex flex-col gap-1">
        <label className="text-gray-400 text-sm font-medium">Forsidebilde</label>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/webp"
          onChange={handleFileChange}
          className="hidden"
        />
        {converting ? (
          <div className="aspect-video rounded-lg border-2 border-dashed border-gray-600 flex flex-col items-center justify-center gap-2">
            <LoadingDots />
            <span className="text-gray-500 text-sm">Konverterer...</span>
          </div>
        ) : coverPreview || currentCoverUrl ? (
          <div
            className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
            onClick={() => fileInputRef.current?.click()}
          >
            <img
              src={coverPreview ?? currentCoverUrl!}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
              <span className="text-white opacity-0 group-hover:opacity-100 font-medium text-sm transition-opacity">Endre</span>
            </div>
            {currentCoverUrl && !coverPreview && (
              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                Nåværende forside
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="aspect-video rounded-lg border-2 border-dashed border-gray-600 hover:border-gray-400 flex flex-col items-center justify-center gap-2 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-8 h-8 text-gray-500">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 18.75h18M3 9.75A2.25 2.25 0 015.25 7.5h13.5A2.25 2.25 0 0121 9.75v8.25" />
            </svg>
            <span className="text-gray-500 text-sm">Velg forsidebilde</span>
          </button>
        )}
      </div>

      {/* Save button */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSave || saving || converting}
        className="
          w-full py-3 rounded-xl
          bg-blue-500 hover:bg-blue-600
          disabled:bg-gray-700 disabled:cursor-not-allowed
          text-white font-semibold
          transition-colors duration-200
          flex items-center justify-center gap-2
        "
      >
        {(saving || converting) && <LoadingDots />}
        {converting ? 'Konverterer...' : saving ? 'Lagrer...' : 'Lagre endringer'}
      </button>
    </div>
  )
}
